import { useRef, useState, type DragEvent } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function CsvFilePicker({
  onFileChange,
  description,
  disabled = false,
  testId,
}: {
  onFileChange: (file: File | null) => void
  description: string
  disabled?: boolean
  testId?: string
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  function chooseDroppedFile(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setDragActive(false)
    if (!disabled) onFileChange(event.dataTransfer.files.item(0) ?? null)
  }
  return (
    <div
      data-testid={testId}
      className={cn(
        'rounded-md border-2 border-dashed border-border bg-muted/30 px-6 py-10',
        dragActive && 'border-primary bg-accent/50',
      )}
      onDragEnter={(event) => {
        event.preventDefault()
        setDragActive(!disabled)
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragActive(false)}
      onDrop={chooseDroppedFile}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-primary">
          <Upload className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="space-y-0.5">
          <p className="text-body-sm text-foreground">Drag a CSV here or browse</p>
          <p className="text-caption text-muted-foreground">{description}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          disabled={disabled}
          onClick={() => fileInput.current?.click()}
        >
          Browse files
        </Button>
        <input
          ref={fileInput}
          aria-label="CSV file"
          type="file"
          accept=".csv,text/csv"
          disabled={disabled}
          className="sr-only"
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        />
      </div>
    </div>
  )
}
